import {
  Component,
  Input,
  Output,
  EventEmitter,
  OnChanges,
  SimpleChanges,
  OnInit,
  ViewChild,
  ElementRef,
  DestroyRef,
  inject,
  computed,
  ChangeDetectionStrategy
} from '@angular/core';

import { ReactiveFormsModule, FormGroup, Validators, FormBuilder } from '@angular/forms';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { finalize, filter } from 'rxjs/operators';
import { forkJoin, of } from 'rxjs';
import { Router } from '@angular/router';
import { HasPermissionDirective } from '@olv/ui-shared/directives/has-permission.directive';
import { Resources, Scopes, Resource } from '@olv/ui-shared/models/settlement-permissions.model';
import { WalletDto, CurrencyOption, WalletStatusEnum, WalletTypeEnum } from '@olv/ui-shared/models/wallet.model';
import { MerchantService } from '@olv/ui-shared/services/merchant.service';
import { NetworkMetadataService } from '@olv/ui-shared/services/network-metadata.service';
import { WalletService } from '@olv/ui-shared/services/wallet.service';
import { BlockchainDropdownComponent } from '@olv/ui-shared/ui-shared/blockchain-dropdown/blockchain-dropdown.component';
import { DropdownComponent } from '@olv/ui-shared/ui-shared/dropdown/dropdown.component';
import { FormContainerComponent } from '@olv/ui-shared/ui-shared/form-container/form-container.component';
import { TextInputComponent } from '@olv/ui-shared/ui-shared/text-input/text-input.component';
import { FormErrorService } from '@olv/ui-shared/services/form-error.service';
import { SystemService } from '@olv/ui-shared/services/system.service';
import { ValidationService } from '@olv/ui-shared/services/validation.service';
import { ToastService } from '@olv/ui-shared/ui-shared/toast/toast.service';
import { MerchantTransactionService } from '@olv/ui-shared/services/merchant-transaction.service';
import { Merchant, MerchantEntity, MerchantEntityType } from '@olv/ui-shared/models/merchant.model';
import { ButtonComponent } from '@olv/ui-shared/ui-shared/button/button.component';

export interface DepositFormData {
  walletId?: string;
  entityName: string;
  walletAddress: string;
  qrCodeData: string;
  networkType?: number;
  amount?: string;
  currency?: string;
  userType?: string;
  identifier?: string;
  walletType?: string;
  blockchainLabel?: string;
}

@Component({
  selector: 'app-deposit-form',
  standalone: true,
  imports: [
    ReactiveFormsModule,
    FormContainerComponent,
    MatProgressSpinnerModule,
    DropdownComponent,
    BlockchainDropdownComponent,
    TextInputComponent,
    HasPermissionDirective,
    ButtonComponent
],
  templateUrl: './deposit-form.component.html',
  styleUrls: ['./deposit-form.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
})
export class DepositFormComponent implements OnInit, OnChanges {
  @Input() walletResource: Resource = Resources.MerchantWallets;
  @Input() transactionResource: Resource = Resources.MerchantTransactions;
  @Input() formTitle = 'Deposit';
  @Input() submitButtonText = 'Next';
  @Input() walletGenerationText = 'Generate Wallet';
  @Input() enableWalletGeneration = true;
  @Input() entityId = '';
  @Input() isSettlementApp = false;
  @Input() wallets: WalletDto[] = [];
  @Input() merchant?: Merchant;
  @Input() walletSelected: WalletDto | null = null;

  @Output() formSubmitted = new EventEmitter<DepositFormData>();
  @Output() blockchainChanged = new EventEmitter<number | null>();
  @Output() walletCreated = new EventEmitter<WalletDto>();
  @Output() currencyChanged = new EventEmitter<string | undefined>();
  @Output() walletChanged = new EventEmitter<WalletDto>();

  @ViewChild('walletDropdown') walletDropdown!: DropdownComponent;
  @ViewChild('walletGroup') private walletGroup?: ElementRef<HTMLElement>;

  scrollWalletIntoView(): void {
    const el = this.walletGroup?.nativeElement;
    if (!el) return;

    const rect = el.getBoundingClientRect();
    const viewportHeight = window.innerHeight || document.documentElement.clientHeight;
    if (rect.top >= 0 && rect.bottom <= viewportHeight) return;

    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    el.scrollIntoView({
      behavior: prefersReducedMotion ? 'auto' : 'smooth',
      block: 'center',
      inline: 'nearest',
    });
  }

  protected readonly Resources = Resources;
  protected readonly Scopes = Scopes;

  private readonly exchangeRates = new Map<string, { rate: number; precision: number }>();
  private readonly formErrorService = inject(FormErrorService);
  private readonly walletService = inject(WalletService);
  private readonly router = inject(Router);
  private readonly merchantService = inject(MerchantService);
  private readonly systemService = inject(SystemService);
  private readonly fb = inject(FormBuilder);
  private readonly destroyRef = inject(DestroyRef);
  private readonly toastrService = inject(ToastService);
  private readonly transactionService = inject(MerchantTransactionService);
  private readonly metadata = inject(NetworkMetadataService);

  depositForm!: FormGroup;

  readonly blockchainOptions = computed(() => [
    { value: null as number | null, label: 'Select Blockchain' },
    ...this.metadata.networks().map((network) => ({ value: network.networkType as number | null, label: network.name })),
  ]);

  userTypeOptions: {
    value: number | null;
    label: string;
  }[] = [
    { value: null, label: 'Select User Type' },
    { value: MerchantEntityType.Client, label: 'Client' },
    { value: MerchantEntityType.Service, label: 'Service' },
  ];
  walletTypeOptions: {
    value: number;
    label: string;
  }[] = [
    { value: WalletTypeEnum.OnetimeUse, label: 'One Time Use' },
    { value: WalletTypeEnum.Dedicated, label: 'Dedicated' },
  ];

  identifierOptions: {
    value: string;
    label: string;
  }[] = [];

  merchantEntities: MerchantEntity[] = [];

  walletOptions: { value: string; label: string }[] = [];
  currencyOptions: CurrencyOption[] = [];

  loading = false;
  generatingWallet = false;
  generatedNetworks = new Set<number>();
  generatingClientOrService = false;
  ngOnInit(): void {
    this.metadata.load();
    this.initializeForms();
    this.loadData();
    this.setupFormSubscriptions();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['wallets'] && !changes['wallets'].firstChange) {
      this.filterWalletsByNetwork();
    }

    if (changes['walletSelected'] && this.walletSelected && this.depositForm) {
      this.depositForm.get('blockchain')?.setValue(this.walletSelected.networkType);
      this.depositForm.get('selectedWallet')?.setValue(this.walletSelected.id);

      this.updateCurrencyOptions(this.walletSelected);
      this.setDefaultCurrency();
    }
  }

  private initializeForms(): void {
    const amountValidators = [
      Validators.max(999999999),
      ValidationService.amountValidator(() => {
        const currency = this.depositForm?.get('selectedCurrency')?.value;
        return this.exchangeRates.get(currency?.toUpperCase())?.precision ?? 2;
      }),
    ];

    const formConfig = this.isSettlementApp
      ? {
          blockchain: [null, Validators.required],
          selectedWallet: [{ value: '', disabled: true }, Validators.required],
          selectedCurrency: [{ value: null, disabled: true }, Validators.required],
          amount: [null, amountValidators],
        }
      : {
          userType: [null, Validators.required],
          identifier: [null, Validators.required],
          blockchain: [null, Validators.required],
          walletType: [null, Validators.required],
          selectedCurrency: [{ value: null, disabled: true }],
          amount: [null, amountValidators],
        };

    this.depositForm = this.fb.group(formConfig);
  }

  private loadData(): void {
    this.loading = true;
    forkJoin({
      rates: this.systemService.getExchangeRates(),
      entities: this.isSettlementApp ? of([]) : this.merchantService.getMerchantEntities(),
    })
      .pipe(finalize(() => (this.loading = false)))
      .subscribe({
        next: ({ rates, entities }) => {
          rates.exchangeRates.forEach((rate) =>
            this.exchangeRates.set(rate.currencySymbol, {
              rate: rate.exchangeRateToBase,
              precision: rate.currencyDisplayPrecision,
            }),
          );

          this.merchantEntities = entities;
        },
        error: (err) => console.error('Form initialization failed', err),
      });
  }

  private setupFormSubscriptions(): void {
    this.depositForm
      .get('blockchain')
      ?.valueChanges.pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((value) => {
        this.blockchainChanged.emit(value);

        if (this.isSettlementApp) {
          this.depositForm.get('selectedWallet')?.reset();
          this.depositForm.get('selectedCurrency')?.reset();
          this.filterWalletsByNetwork();
        }
        this.depositForm.get('amount')?.reset();
        this.updateCurrencyOptions(null);
      });

    this.depositForm
      .get('selectedWallet')
      ?.valueChanges.pipe(
        takeUntilDestroyed(this.destroyRef),
        filter((value) => !!value),
      )
      .subscribe((walletId) => {
        const wallet = this.wallets.find((wallet) => wallet.id === walletId);
        if (!wallet) return;

        this.updateCurrencyOptions(wallet);
        this.walletChanged.emit(wallet);
      });

    this.depositForm
      .get('selectedCurrency')
      ?.valueChanges.pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((symbol) => {
        this.currencyChanged.emit(symbol ?? undefined);
      });

    this.depositForm
      .get('userType')
      ?.valueChanges.pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((userType) => {
        this.depositForm.get('identifier')?.reset();
        this.identifierOptions = this.getIdentifierOptions(userType);
      });

    this.depositForm
      .get('amount')
      ?.valueChanges.pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((amount) => {
        if (amount === '') {
          this.onResetAmount();
        }
      });
  }

  getFieldError(fieldName: string, label: string, customMessages?: { [key: string]: string }): string {
    const control = this.depositForm.get(fieldName);

    return this.formErrorService.getErrorMessage(control, label, customMessages);
  }

  private filterWalletsByNetwork(): void {
    const blockchain = this.depositForm.get('blockchain')?.value;
    if (blockchain === null) {
      this.walletOptions = [];
      this.depositForm.get('selectedWallet')?.disable();
      if (this.isSettlementApp) this.depositForm.get('selectedCurrency')?.disable();
      return;
    }

    this.depositForm.get('selectedWallet')?.enable();

    const filtered = this.wallets.filter(
      (wallet) => wallet.networkType === blockchain && wallet.status === WalletStatusEnum.Active,
    );
    this.walletOptions = filtered.map((wallet) => ({ value: wallet.id, label: this.formatWalletLabel(wallet) }));

    if (this.walletOptions.length === 0 && blockchain) {
      this.depositForm.get('selectedWallet')?.disable();
    } else {
      this.depositForm.get('selectedWallet')?.enable();
    }
  }

  private updateCurrencyOptions(wallet: WalletDto | null): void {
    const network = this.isSettlementApp ? wallet?.networkType : this.depositForm.get('blockchain')?.value;
    const currency = this.depositForm.get('selectedCurrency');
    const amount = this.depositForm.get('amount');

    if (!network) {
      this.currencyOptions = [];
      currency?.disable({ emitEvent: false });
      amount?.disable({ emitEvent: false });
      return;
    }

    this.currencyOptions = this.metadata.currencyOptionsOf(network);

    if (this.currencyOptions.length > 0) {
      currency?.enable({ emitEvent: false });
      amount?.enable({ emitEvent: false });

      if (!this.isSettlementApp) {
        this.setDefaultCurrency();
      }
    }
  }

  private setDefaultCurrency(): void {
    this.depositForm.get('selectedCurrency')?.setValue(this.currencyOptions[0]?.value ?? null);
  }

  private formatWalletLabel(wallet: WalletDto): string {
    if (!wallet.address) return 'No address';
    return `${wallet.address.substring(0, 7)}...${wallet.address.substring(wallet.address.length - 7)}`;
  }

  generateWallet(): void {
    const blockchain = this.depositForm.get('blockchain')?.value;
    if (!blockchain) return;
    this.generatingWallet = true;

    const walletCreation$ = this.router.url.includes('/dashboard')
      ? this.walletService.generateSystemDepositWallet(blockchain)
      : this.walletService.generateMerchantDepositWallet(this.entityId, blockchain);

    walletCreation$.pipe(finalize(() => (this.generatingWallet = false))).subscribe({
      next: (result) => {
        if (!result) return;

        this.generatedNetworks.add(blockchain);
        this.filterWalletsByNetwork();
        this.depositForm.get('selectedWallet')?.setValue(result.id);
        this.walletCreated.emit(result);
        this.toastrService.success('Wallet generated successfully');
      },
      error: () => {
        this.toastrService.error('Failed to generate wallet');
      },
    });
  }

  onSubmit(): void {
    if (this.depositForm.invalid) {
      return;
    }

    const formValue = this.depositForm.getRawValue();

    if (!this.isSettlementApp) {
      this.prepareDepositDetails(formValue);
      return;
    }

    const wallet = this.wallets.find((wallet) => wallet.id === formValue.selectedWallet);

    if (wallet) {
      this.formSubmitted.emit({
        walletId: wallet.id,
        entityName: wallet.merchantName || 'Selected Entity',
        walletAddress: wallet.address,
        qrCodeData: wallet.address,
        networkType: wallet.networkType,
        amount: formValue.amount ? Number(formValue.amount).toString() : undefined,
        currency: formValue.selectedCurrency,
      });
    }
  }

  onAddIdentifier(name: string): void {
    const userType = this.depositForm.get('userType')?.value;
    if (!userType) return;

    this.generatingClientOrService = true;
    this.merchantService.createMerchantEntity({ type: userType, name }).subscribe({
      next: (entity) => {
        this.merchantEntities.push(entity);
        this.identifierOptions = this.getIdentifierOptions(userType);
        this.depositForm.get('identifier')?.setValue(entity.id);
        this.toastrService.success(`${this.identifierLabel} created successfully`);
        this.generatingClientOrService = false;
      },
      error: () => {
        this.toastrService.error(`Failed to create ${this.identifierLabel.toLowerCase()}`);
        this.generatingClientOrService = false;
      },
    });
  }

  getIdentifierOptions(userType: MerchantEntityType | null): { value: string; label: string }[] {
    if (!userType) return [];

    const entities = this.merchantEntities.filter((e) => e.type === userType);
    if (!entities.length) return [];

    return entities.map((entity) => ({
      value: entity.id,
      label: entity.name,
    }));
  }

  private prepareDepositDetails(formValue: any): void {
    const blockchain = formValue.blockchain;
    const blockchainOption = this.blockchainOptions().find((option) => option.value === blockchain);
    const currency = this.findCurrencyBySymbol(formValue.selectedCurrency);

    this.transactionService
      .prepareDeposit({
        merchantEntityId: formValue.identifier,
        blockchain,
        walletType: formValue.walletType,
        currencyId: currency?.id ?? '',
        amount: formValue.amount ? Number(formValue.amount) : undefined,
      })
      .subscribe({
        next: (result) => {
          this.formSubmitted.emit({
            entityName: this.merchant?.name || 'Merchant',
            walletAddress: result.walletAddress,
            qrCodeData: result.walletAddress,
            networkType: blockchain,
            amount: formValue.amount ? formValue.amount : undefined,
            currency: formValue.selectedCurrency,
            userType: this.identifierLabel,
            identifier: result.identifier,
            walletType: formValue.walletType,
            blockchainLabel: blockchainOption?.label,
          });
        },
        error: () => {
          this.toastrService.error('Failed to prepare deposit details');
        },
      });
  }

  private findCurrencyBySymbol(symbol: string): CurrencyOption | undefined {
    return this.currencyOptions.find((c) => c.value === symbol);
  }

  get isAmountDisabled(): boolean {
    return !this.depositForm.get('selectedCurrency')?.value;
  }

  get currencyPrecision(): number {
    const currency = this.depositForm.get('selectedCurrency')?.value;
    return this.exchangeRates.get(currency?.toUpperCase())?.precision ?? 2;
  }

  get isGenerateDisabled(): boolean {
    const blockchain = this.depositForm.get('blockchain')?.value;
    return this.generatingWallet || !blockchain || this.generatedNetworks.has(blockchain);
  }

  get isDisabled(): boolean {
    return this.depositForm.invalid || this.generatingWallet || this.loading;
  }

  get submitButtonState(): 'disabled' | 'default' {
    return this.isDisabled ? 'disabled' : 'default';
  }

  onResetAmount(): void {
    this.depositForm.get('amount')?.setValue(null);
    this.depositForm.get('amount')?.markAsUntouched();
    this.depositForm.get('amount')?.updateValueAndValidity();
  }

  get identifierLabel(): string {
    const userType = this.depositForm.get('userType')?.value;
    if (userType === MerchantEntityType.Client) return 'Client';
    if (userType === MerchantEntityType.Service) return 'Service';
    return '';
  }
}
